import React, {useEffect, useImperativeHandle, useRef} from 'react'
import {Box, mergeRefs} from '@chakra-ui/react3'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {useGesture} from '@use-gesture/react'
import useZoomGestures from '../../../hooks/use-zoom-gestures'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'

const ZOOM_SCALE = 1.5
const ZOOM_IN_CURSOR = getAssetUrl('static/ico/zoom-in-cursor.svg')
const ZOOM_OUT_CURSOR = getAssetUrl('static/ico/zoom-out-cursor.svg')

/**
 * Wraps media with zoom and pan gesture support while preserving keyboard toggle behavior.
 * The forwarded ref points to the container DOM element.
 * Use `zoomImperativeRef` to access
 * imperative zoom controls like `reset()` and `toggleScale()`.
 *
 * @param {object} props Component props.
 * @param {boolean} props.enabled Whether zoom interactions are enabled.
 * @param {React.ReactNode} props.children Zoomable content.
 * @param {string} props.imageName Accessible image name used in assistive labels.
 * @param {Function} props.onZoomChange Called when zoom state changes.
 * @param {Function} props.onClick Called on click before internal keyboard toggle handling.
 * @param {React.RefObject<{reset: Function, toggleScale: Function}>} props.zoomImperativeRef Ref for imperative zoom controls.
 * @param {React.Ref<HTMLElement>} ref Forwarded ref to the container DOM element.
 * @returns {JSX.Element} Zoomable media container.
 */
const ZoomImage = React.forwardRef(
    (
        {enabled = true, children, imageName, onZoomChange, onClick, zoomImperativeRef, ...props},
        ref
    ) => {
        const {formatMessage} = useIntl()
        const containerRef = useRef(null)
        const zoomableRef = useRef(null)

        const {state, reset, toggleScale, gestureHandlers, gestureConfig} = useZoomGestures({
            enabled,
            maxScale: ZOOM_SCALE,
            zoomableElementRef: zoomableRef,
            containerRef
        })

        useGesture(gestureHandlers, gestureConfig)

        useEffect(() => {
            onZoomChange?.(state.isZoomed)
        }, [onZoomChange, state.isZoomed])

        useImperativeHandle(zoomImperativeRef, () => ({reset, toggleScale}), [reset, toggleScale])

        const mergedRef = mergeRefs(ref, containerRef)

        const stopIfZoomed = (e) => {
            if (state.isZoomed) e.stopPropagation()
        }

        const cursor = enabled
            ? `url("${state.isZoomed ? ZOOM_OUT_CURSOR : ZOOM_IN_CURSOR}") 18 18, auto`
            : undefined

        return (
            <Box
                as={enabled ? 'button' : 'div'}
                type={enabled ? 'button' : undefined}
                ref={mergedRef}
                // Keep this class so the native Chakra focus ring can be suppressed for the zoomed state.
                className="leap-zoom-image"
                overflow="hidden"
                position="relative"
                display="block"
                width="100%"
                height="100%"
                padding="0"
                border="0"
                background="transparent"
                appearance="none"
                textAlign="inherit"
                cursor={cursor}
                _focusVisible={
                    state.isZoomed
                        ? {
                              // The native outline is drawn outside the button, so when the zoomed
                              // image expands inside an overflow-hidden container it becomes clipped.
                              // Used an inset pseudo-element instead so the focus ring stays visible.
                              outline: 'none',
                              _after: {
                                  content: '""',
                                  position: 'absolute',
                                  inset: '2px',
                                  pointerEvents: 'none',
                                  borderRadius: 'm',
                                  borderWidth: '1px',
                                  borderStyle: 'solid',
                                  borderColor: 'border.strong'
                              }
                          }
                        : {
                              outlineColor: 'border.strong'
                          }
                }
                onClick={(e) => {
                    onClick?.(e)

                    // Skip zoom when the feature is disabled or when an external
                    // click handler has explicitly cancelled the default interaction.
                    if (!enabled || e.defaultPrevented) {
                        return
                    }

                    // Real pointer clicks usually include meaningful viewport coordinates.
                    // Keyboard and assistive technology activation often do not.
                    const hasPointerCoordinates = e.clientX !== 0 || e.clientY !== 0

                    // Preserve click-centered zoom for mouse and touch.
                    // Fall back to center-based zoom for keyboard and screen reader activation.
                    toggleScale(
                        hasPointerCoordinates
                            ? {
                                  x: e.clientX,
                                  y: e.clientY
                              }
                            : undefined
                    )
                }}
                onTouchStart={(e) => {
                    props.onTouchStart?.(e)
                    stopIfZoomed(e)
                }}
                onTouchMove={(e) => {
                    props.onTouchMove?.(e)
                    stopIfZoomed(e)
                }}
                onTouchEnd={(e) => {
                    props.onTouchEnd?.(e)
                    stopIfZoomed(e)
                }}
                aria-label={
                    enabled
                        ? formatMessage(
                              state.isZoomed
                                  ? {
                                        id: 'leap.zoom_image.assistive_msg.zoom_out',
                                        defaultMessage: 'Zoom out {imageName}'
                                    }
                                  : {
                                        id: 'leap.zoom_image.assistive_msg.zoom_in',
                                        defaultMessage: 'Zoom in {imageName}'
                                    },
                              {imageName: imageName || ''}
                          )
                        : undefined
                }
                {...props}
            >
                <Box
                    ref={zoomableRef}
                    style={{
                        transform: enabled ? state.transform : undefined,
                        touchAction: enabled
                            ? state.isZoomed
                                ? 'none'
                                : 'pan-x pan-y'
                            : undefined,
                        transition: 'transform 220ms ease'
                    }}
                >
                    {children}
                </Box>
            </Box>
        )
    }
)

if (process.env.NODE_ENV !== 'production') {
    ZoomImage.displayName = 'ZoomImage'
}

ZoomImage.propTypes = {
    enabled: PropTypes.bool,
    imageName: PropTypes.string,
    children: PropTypes.node,
    onClick: PropTypes.func,
    onZoomChange: PropTypes.func,
    zoomImperativeRef: PropTypes.shape({current: PropTypes.any}),
    onTouchStart: PropTypes.func,
    onTouchMove: PropTypes.func,
    onTouchEnd: PropTypes.func
}

export default ZoomImage
